import './styles.scss';
import React, {Dispatch, useEffect, useLayoutEffect} from "react";

/* Types, Constants, Utils */
import * as Constants from "../../constants";
import {PageContext} from "../../types/app";
import { CardContext } from '../../types/document';
import SearchUtils from "../../utils/search-utils";
import Utils from "../../utils";
import {TQueryItem,} from "../../constants";

/* DATA */
import URL_FORWARDING from "../../assets/data/url-forwarding.json";

/* Bootstrap */
import {Container} from "react-bootstrap";

/* Redux */
import {IPrevSearchState, ISearchState} from "../../types/redux/search";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../redux";
import {AnyAction} from "@reduxjs/toolkit";
import {setContentCategory} from "../../redux/slices/content-slice";
import {
  addRemoveSelectedFilters,
  applySelectedFilters, resetFilters, searchReset,
  setDBSet, setFiltersKey,
  setIndustry,
  setQuery,
  setSearch, setSearchDates, setSelectedFilters, suspendSearch,
} from "../../redux/slices/search-slice";

/* Components */
import IndustryNavbar from "../../components/industry-navbar";
import Search from "../../components/search/search-cmp";
import ViewScrollContainer from "../../components/view/view-scroll-container";
import ContentPage from "../content/components/page";

/* Hooks */
import { useCardContext } from "../../hooks";
import {useLocation, useNavigate} from "react-router-dom";
import {clearResults, resetResults} from "../../redux/slices/result-slice";
import SearchService from "../../api/search";

const Industries = ():JSX.Element => {
  /* redux */
  const searchState: ISearchState = useSelector<RootState, ISearchState>((state: RootState):any => state.search);
  const prevSearchState: IPrevSearchState = useSelector<RootState, IPrevSearchState>(state => state.prev_search);
  const location = useLocation();

  /* context */
  const cardContext: string = useCardContext().context;

  /* hooks */
  const dispatch:Dispatch<AnyAction> = useDispatch();
  const navigate = useNavigate();

  useEffect(()=> {
    /*  since /:industry is an acceptable route need to check for invalid urls here */
    const isValidIndustry = (): boolean => {
      const industry: string = Utils.convertToKeyOrId(window.location.pathname.split('/')[1]);
      return Constants.INDUSTRY_ITEMS.findIndex(i => {
        return i.industry === industry
      }) !== -1
    }

    const legacyUrl = (urlSegments: string[]):void =>{
      /* redirect legacy url */
      dispatch(suspendSearch(true))

      const legacyParams: string = window.location.hash.substring(1,window.location.hash.length);
      const transposedParams: URLSearchParams = SearchUtils.transposeLegacyParams(legacyParams);
      dispatch(setDBSet(Constants.DOCUMENTS));
      dispatch(setFiltersKey(Constants.DOCUMENTS));

      let industry: string;
      if (transposedParams.get(Constants.INDUSTRY)) {
        industry = transposedParams.get(Constants.INDUSTRY);
      } else {
        industry = (urlSegments[2] === Constants.RESULTS) ? urlSegments[1] : Constants.ALL_INDUSTRIES;
      }
      dispatch(setIndustry(industry));

      const query:TQueryItem[] = SearchUtils.convertQueryParams(transposedParams.get(Constants.QUERY_KEY))
      dispatch(setQuery({query:query, recent_query:false}));

      dispatch(setSearchDates(SearchUtils.convertDateParams(transposedParams.get(Constants.DATES_KEY))))
      if (transposedParams.get(Constants.FILTERS_KEY)) {
        dispatch(setSelectedFilters(transposedParams.get(Constants.FILTERS_KEY).split(',')))
      }
      dispatch(applySelectedFilters());

      const url: string = '/' + industry + '/'+ Constants.DOCUMENTS + '/?' + transposedParams.toString();
      window.history.replaceState(null, '', url);
    }

    const handleTidUrl = async ():Promise<any> =>{
      let urlHrefSegments: string[] = window.location.href.split('/');
      const tid: string = urlHrefSegments[urlHrefSegments.length - 1].length > 0 ?  urlHrefSegments[urlHrefSegments.length - 1] : urlHrefSegments[urlHrefSegments.length - 2];
      const response: any = await SearchService.solrTIDQuery(tid);
      if (response.response.numFound > 0) {
        const recordId: string = response.response.docs[0].id;
        navigate(`/all-industries/documents/${Constants.VIEWER}/?iid=${recordId}&id=${recordId}&q=null,all,contains,*:*&db-set=documents&sort=relevance&pg=1&npp=20&industry=all-industries&rtool=metadata`)
      }
    }

    const handlePermalink = (): void=> {
      let urlHrefSegments: string[] = window.location.href.split('/');
      if (isValidIndustry()) {
        if (urlHrefSegments[4] === Constants.DOCS) {
          const recordId: string = urlHrefSegments[5].split('=')[1];
          navigate(`/${urlHrefSegments[3]}/documents/${Constants.VIEWER}/?iid=${recordId}&id=${recordId}&q=null,all,contains,*:*&db-set=documents&sort=relevance&pg=1&npp=20&industry=${urlSegments[1]}&rtool=metadata`)
        }
      } else if (urlHrefSegments[3] === Constants.DOCS) {
        let recordId: string =  urlHrefSegments[4]
        if (urlHrefSegments[4].indexOf('#') !== -1) {
         recordId = urlHrefSegments[4].split('=')[1];
        }
        navigate(`/all-industries/documents/${Constants.VIEWER}/?iid=${recordId}&id=${recordId}&q=null,all,contains,*:*&db-set=documents&sort=relevance&pg=1&npp=20&industry=all-industries&rtool=metadata`)
      }
    }

    let urlSegments: string[] = window.location.pathname.split('/');

    if (location.pathname.includes(`/${Constants.TID}/`)) {
      handleTidUrl();
    } else if (location.pathname.includes(`/${Constants.DOCS}/`)) {
      handlePermalink();
    } else if ((urlSegments[1] === Constants.RESULTS) || (urlSegments[2] === Constants.RESULTS)) {
      legacyUrl(urlSegments)
    }  else {
      const urlToMap: string = location.pathname[location.pathname.length - 1] === '/' ? location.pathname : location.pathname + '/';
      const mappedUrl: string = URL_FORWARDING[urlToMap];
      if (mappedUrl){
        navigate(mappedUrl)
      } else {
        //console.log('No redirect')
      }
    }

    return ()=> {
      if (!window.location.pathname.includes('/' + Constants.VIEWER + '/')) {
        dispatch(searchReset());
        dispatch(resetResults())
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useLayoutEffect(()=> {
    const captureBackArrow = (): void => {
      const segments: string[] = window.location.href.split('/');
      if ((segments[segments.length-2].indexOf(Constants.DOCUMENTS) !== -1) || (segments[segments.length-2].indexOf(Constants.BIBLIOGRAPHY)  !== -1)) {
        dispatch(setSearch(SearchUtils.fromDataSource(window.location.search)));
        dispatch(applySelectedFilters());
      }
    }
    window.addEventListener('popstate', captureBackArrow);

    return ()=>{
      window.removeEventListener('popstate', captureBackArrow);
    }
  }, [dispatch])

  useEffect(()=>{
    /* internal or external w/ filters */
     const searchParams: URLSearchParams = new URLSearchParams(window.location.search);
     if (searchParams.get(Constants.DATES_KEY) || (searchParams.get(Constants.FILTERS_KEY)
       && (searchParams.get(Constants.FILTERS_KEY).split(',') !== searchState.selected_filters_ids))) {
       if (searchState.available_filter_options.length === 0) {
         //console.log('internal or external w/ filters')
         const searchParams: URLSearchParams = new URLSearchParams(window.location.search);
         const searchState: ISearchState = SearchUtils.fromDataSource(searchParams.toString());

         dispatch(setSearch(searchState));
         dispatch(clearResults());

         const filters: string = searchParams.get(Constants.FILTERS_KEY);
         const dates: string = searchParams.get(Constants.DATES_KEY);
         if (filters) {
           dispatch(suspendSearch(true));
           dispatch(setSelectedFilters(filters.split(',')));
           /* might not be available before so suspendSearch till loaded from solr */
         }
         if (dates) {
           const ids: string[] = dates.split(';').map((d:string) => {
             return d.split(',')[0]
           })
           if (ids.indexOf('') !== -1) {
             ids.splice(ids.indexOf(''), 1);
             dispatch(addRemoveSelectedFilters({ids, method: Constants.ADD}))
           }
         }
       }
     } else {
       const newSearchState: ISearchState = SearchUtils.fromDataSource(window.location.search);
       if (newSearchState.query.length !== 0) { /* case picked up by default search block below */
         dispatch(setSearch(SearchUtils.fromDataSource(window.location.search)));
         dispatch(applySelectedFilters());
         dispatch(resetResults())
       }
       dispatch(suspendSearch(false));
     }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [dispatch, location.pathname]);


  useEffect(()=>{
    /* Case 1: default initial search when arriving via home page menu or navbar selection*/
    if ((searchState.query.length !== 0) || (searchState.suspendSearch)) return;

    if (searchState.db_set === Constants.DOCUMENTS) {
      //console.log('DEFAULT SEARCH STATE :  Constants.DOCUMENTS')
      if (prevSearchState.document) {
        dispatch(setSearch({...prevSearchState.document}));
        dispatch(setContentCategory(null));
      } else {
        dispatch(setQuery({query: [Constants.DEFAULT_SEARCH], recent_query: false}))
        dispatch(resetFilters());
      }
    } else if (searchState.db_set === Constants.BIBLIOGRAPHY) {
      //console.log('DEFAULT SEARCH STATE :  Constants.BIBLIOGRAPHY')
      if (prevSearchState.bibliography) {
        dispatch(setSearch({...prevSearchState.bibliography}));
        dispatch(setContentCategory(null));
      } else {
        dispatch(setQuery({query: [Constants.DEFAULT_SEARCH], recent_query: false}));
        dispatch(resetFilters());
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState.db_set, searchState.suspendSearch]);

 /* const pageContent = useCallback((): JSX.Element=>{
    if (searchState.db_set) {
      // Search bar when in a db set
      return <Search/>
    } else {
      // Content page --> db_set === null
      return <ContentPage />
    }
  }, [searchState.db_set])*/

  return (
    <div className={'industries view'}>
      <PageContext.Provider value={{context: Constants.INDUSTRY}}>
        <CardContext.Provider value={{context:cardContext}}>
          <ViewScrollContainer>
            <Container className={'px-0 px-lg-4'}>
              <IndustryNavbar  />

              {/* Content page --> db_set === null */}
              {!searchState.db_set &&
                <ContentPage />}

              {/* Search bar when in a db set*/}
              {searchState.db_set &&
                <Search/>}

              {(searchState.industry === Constants.TOBACCO) &&
                <div className={'partners mt-5 d-flex mx-3'}>
                  <img className={'me-3'} src="/assets/images/ucsf-logo-with-tag-line.svg" alt="" height="55"/>
                  <div className={'vertical-hr ms-2 me-4'}/>
                  <img src="/assets/images/truth-logo-2020-color.svg" alt="" height="48"/>
                </div>}

              {(searchState.industry === Constants.OPIOIDS) &&
                <div className={'partners mt-5 d-flex mx-3'}>
                  <img className={'me-3'} src="/assets/images/ucsf-logo-with-tag-line.svg" alt="" height="55"/>
                  <div className={'vertical-hr ms-2 me-4'}/>
                  <img src="/assets/images/johns-hopkins-university-logo.svg" alt="" height="160"/>
                </div>}
            </Container>
          </ViewScrollContainer>
        </CardContext.Provider>
      </PageContext.Provider>
    </div>
  )
}

export default Industries;
